import React, { useContext, useState, useEffect } from 'react';
import {
  AutocompleteInput,
  BooleanInput,
  Button,
  FormDataConsumer,
  FormWithRedirect,
  ReferenceInput,
  required,
  SaveButton,
  TextInput,
  useGetList,
  useNotify,
} from 'react-admin';
import Grid from '@material-ui/core/Grid';
import { Box, Checkbox } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import DialogActions from '@material-ui/core/DialogActions';
import ClearIcon from '@material-ui/icons/Clear';
import { disableAutofillOptions, getZ3PConfigValue } from '../helper';
import SearchLocationInput from '../tabs/customer/SearchLocationInput';
import AddressListSelection from './ExistingCustomer/AddressListSelection';
import ReferenceChild from '../components/ReferenceChild';
import { QuickAppointmentContext } from '../Context';
import { apiGet, apiPost } from '../brainApi';
import useStyles from './styles';

const AddressForm = ({ addressList }) => {
  const classes = useStyles();
  const notify = useNotify();
  const serviceLocations = getZ3PConfigValue('service_locations');
  const hasWorkplaceServices = serviceLocations?.includes('workplace');
  const checkboxQuestions = getZ3PConfigValue('address_verification_questions');
  const isAddressVerificationQuestionEnabled = getZ3PConfigValue(
    'is_address_verification_question_enabled',
  );
  const {
    customerType,
    handleNext,
    contactData,
    setContactData,
    addressData,
    setAddressData,
    setSavedData,
    isFirstAddressRender,
    setIsFirstAddressRender,
    setNote,
    getSearchData,
    handleClose,
  } = useContext(QuickAppointmentContext);

  const [checkboxValues, setCheckboxValues] = useState(
    checkboxQuestions.map(() => false),
  );

  // If a client location has been selected or only shop services are offered, set the active tab to 'public'
  const [tabActive, setTabActive] = useState(
    addressData?.client_location_id ||
      (serviceLocations?.length === 1 && serviceLocations?.includes('shop'))
      ? 'public'
      : 'private',
  );
  const [isAddingAddress, setIsAddingAddress] = useState(
    customerType === 'new',
  );
  const [address, setAddress] = useState(addressData);
  const [isCreating, setIsCreating] = useState(false);

  const {
    ids: client_location_ids,
    loading: isLoadingClientLocationIds,
  } = useGetList(
    'client-location',
    { page: 1, perPage: 1000 },
    { field: 'id', order: 'ASC' },
    { is_active: true },
  );

  const setPartnerLocation = (values) => {
    apiGet(`/client-location/${values.client_location_id}`).then((json) => {
      setAddressData((prev) => ({
        ...prev,
        ...values,
        client_location_name: json.short_name || json.long_name,
        address_id: json.address_id,
      }));
      handleNext();
    });
  };

  const handleCancel = () => {
    setTabActive('private');
    setIsAddingAddress(false);
  };

  const formatServiceabilityNote = (checkboxVals, serviceabilityNote = '') => {
    const formattedServiceabilityNote = `Customer Notes: "${serviceabilityNote}"${checkboxQuestions.map(
      (q, i) => `\n${checkboxVals[i] ? '' : 'UN'}CONFIRMED: ${q}`,
    )}`;
    return formattedServiceabilityNote;
  };

  const createCustomer = () => {
    setIsCreating(true);
    setNote({ text: 'Creating customer...', type: 'info' });
    const clientLocationData = addressData?.client_location_id
      ? { default_client_location_id: addressData.client_location_id }
      : {};
    return apiPost('/customer', { ...contactData, ...clientLocationData })
      .then((json) => {
        if (json.Status === 'Failure') {
          setNote({ text: json?.Message, type: 'error' });
          console.log('Error: ', json?.Message);
        } else {
          setNote({ text: 'Customer created!', type: 'success' });
          setSavedData((prev) => ({
            ...prev,
            customer_id: json?.id,
          }));
          setContactData((prev) => ({
            ...prev,
            id: json?.id,
          }));
          return json?.id;
        }
      })
      .catch((error) => {
        console.log(error);
        setNote({ text: 'Something went wrong', type: 'error' });
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const createAddress = (customer_id, values, exitAfterCreate = false) => {
    setIsCreating(true);
    setNote({ text: 'Creating address...', type: 'info' });
    if (customer_id && values?.postal_code) {
      apiPost('/customer-address', { ...values, customer_id })
        .then((json) => {
          if (json.Status === 'Failure') {
            setNote({ text: json?.Message, type: 'error' });
            console.log('Error: ', json?.Message);
          } else {
            setNote({ text: 'Customer address created!', type: 'success' });
            setAddressData({
              customer_address_id: json.id,
              address_line_1: json.one_line_human_readable_address,
              city: json.city,
              country: json.country,
            });
            if (customerType === 'existing') {
              getSearchData(customer_id);
            } else if (customerType === 'new') {
              setSavedData((prev) => ({
                ...prev,
                customer_address_id: json.id,
              }));
            }
            if (exitAfterCreate) {
              handleClose();
              notify('Customer created successfully');
            } else {
              handleNext();
            }
          }
        })
        .catch((error) => console.log(error))
        .finally(() => {
          setIsCreating(false);
        });
    }
  };

  const handleSubmit = async (values, exitAfterCreate = false) => {
    const newValues = values;
    let customerId;
    if (customerType === 'new') {
      customerId = await createCustomer();
      if (customerId && !newValues && exitAfterCreate) {
        handleClose();
        notify('Customer created successfully');
        return;
      }
    } else {
      customerId = contactData.id;
    }
    if (isAddressVerificationQuestionEnabled && checkboxQuestions?.length > 0) {
      newValues.serviceability_note = formatServiceabilityNote(
        checkboxValues,
        newValues?.serviceabilityNote,
      );
    }
    if (tabActive === 'public' && newValues?.client_location_id) {
      if (customerType === 'new') {
        setSavedData((prev) => ({
          ...prev,
          client_location_id: newValues?.client_location_id,
          parking_id: newValues?.parking_id,
          keyosk_id: newValues?.keyosk_id,
          box: newValues?.box,
        }));
      }
      setPartnerLocation(newValues);
    } else if (tabActive === 'private' && !isAddingAddress) {
      setAddressData((prev) => {
        const newData = { ...prev, ...newValues };
        delete newData.client_location_id;
        delete newData.parking_id;
        delete newData.keyosk_id;
        delete newData.box;
        return newData;
      });
      handleNext();
    } else {
      createAddress(customerId, newValues, exitAfterCreate);
      setIsAddingAddress(false);
    }
  };

  const mapCheckBoxQuestions = () => {
    return checkboxQuestions.map((q, i) => {
      return (
        <Grid item xs={12} sm={12} md={12}>
          <FormControlLabel
            control={
              <Checkbox
                name={`q${i}`}
                label={q}
                checked={checkboxValues[i]}
                onChange={() => {
                  const newCheckboxValues = checkboxValues.map((v, j) =>
                    i === j ? !v : v,
                  );
                  setCheckboxValues(newCheckboxValues);
                }}
              />
            }
            label={q}
          />
        </Grid>
      );
    });
  };

  // If 'shop' tab is selected and there's only one shop, auto-select it and move to next step, but only for the first render so that if user clicks back to Address section they're able to switch to 'individual' tab
  useEffect(() => {
    if (
      !isLoadingClientLocationIds &&
      tabActive === 'public' &&
      isFirstAddressRender &&
      client_location_ids.length === 1
    ) {
      setIsFirstAddressRender(false);
      handleSubmit({ client_location_id: client_location_ids[0] });
    }
  }, [
    isFirstAddressRender,
    isLoadingClientLocationIds,
    tabActive,
    client_location_ids,
  ]);

  const actionButtons = (handleSubmitWithRedirect, saving, formValues) => (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <DialogActions>
          {((tabActive === 'private' && isAddingAddress) ||
            tabActive === 'public' ||
            customerType === 'new') && (
            <SaveButton
              label="Continue"
              handleSubmitWithRedirect={handleSubmitWithRedirect}
              saving={saving || isCreating}
              disabled={isCreating}
              className={classes.buttonWithoutIcon}
            />
          )}
        </DialogActions>
        {customerType === 'existing' &&
          isAddingAddress &&
          !!addressList.length && (
            <Button onClick={handleCancel} label="Cancel">
              <ClearIcon />
            </Button>
          )}
      </Box>
      {customerType === 'new' && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          <Button
            label="Create Account and Exit"
            onClick={() => {
              handleSubmit(formValues, true);
            }}
            disabled={isCreating}
          />
        </Box>
      )}
    </>
  );

  // If there's only one shop, return null so we don't flash the form while we set the address data
  if (
    isFirstAddressRender &&
    serviceLocations?.length === 1 &&
    serviceLocations?.includes('shop') &&
    client_location_ids?.length === 1
  ) {
    return null;
  }

  return (
    <div className={classes.main}>
      <FormWithRedirect
        resource="customer"
        record={address}
        save={handleSubmit}
        onChange={handleSubmit}
        render={({ handleSubmitWithRedirect, saving, record: formValues }) => (
          <>
            <Grid container spacing={1} className={classes.formRoot}>
              <Grid item xs={12} sm={12} md={12}>
                {serviceLocations?.length > 1 && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      className={`${classes.tabLabel} ${
                        tabActive === 'private' ? `${classes.tabActive}` : ''
                      }`}
                      onClick={() => setTabActive('private')}
                    >
                      Individual
                    </Box>
                    <Box
                      className={`${classes.tabLabel} ${
                        tabActive === 'public' ? `${classes.tabActive}` : ''
                      }`}
                      onClick={() => setTabActive('public')}
                    >
                      {hasWorkplaceServices ? 'Partner' : 'Shop'}
                    </Box>
                  </Box>
                )}
                {tabActive === 'private' && (
                  <Box mb={2}>
                    {(customerType === 'new' ||
                      (customerType === 'existing' && isAddingAddress)) && (
                      <Grid container spacing={1} className={classes.formRoot}>
                        <Grid item xs={12}>
                          <Box mt={3}>
                            <SearchLocationInput
                              setAddress={setAddress}
                              autoFocus
                            />
                          </Box>

                          {address && (
                            <Box>
                              <Grid
                                container
                                spacing={1}
                                className={classes.formRoot}
                              >
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextInput
                                    source="address_line_1"
                                    validate={[required()]}
                                    fullWidth
                                    className="marginLeftRight5"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextInput
                                    source="address_line_2"
                                    fullWidth
                                    className="marginLeftRight5"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextInput
                                    source="city"
                                    validate={[required()]}
                                    className="marginLeftRight5"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextInput
                                    source="state"
                                    validate={[required()]}
                                    className="marginLeftRight5"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextInput
                                    source="postal_code"
                                    validate={[required()]}
                                    className="marginLeftRight5"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <BooleanInput
                                    source="is_primary"
                                    className="marginLeftRight5"
                                  />
                                </Grid>
                                {isAddressVerificationQuestionEnabled &&
                                  checkboxQuestions?.length > 0 && (
                                    <Grid item xs={12} sm={12} md={12}>
                                      <p>
                                        Tell us about your property. This will
                                        help us determine how our vehicles can
                                        best meet your needs.
                                      </p>
                                      {mapCheckBoxQuestions()}
                                      <TextInput
                                        label="Is there anything else you'd like us to know about this location?"
                                        source="serviceabilityNote"
                                        className="marginLeftRight5"
                                      />
                                    </Grid>
                                  )}
                              </Grid>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    )}
                    {customerType === 'existing' && !isAddingAddress && (
                      <Box>
                        <Grid container>
                          <AddressListSelection
                            addressList={addressList}
                            setAddress={setAddress}
                            setIsAddingAddress={setIsAddingAddress}
                            handleSubmit={handleSubmit}
                          />
                        </Grid>
                      </Box>
                    )}
                  </Box>
                )}
                {tabActive === 'public' && (
                  <Box mb={2}>
                    <Grid container spacing={1} className={classes.formRoot}>
                      <Grid item xs={12} sm={12} md={12}>
                        <ReferenceInput
                          source="client_location_id"
                          label={
                            hasWorkplaceServices ? 'Client location' : 'Shop'
                          }
                          reference="client-location"
                          validate={[required()]}
                          perPage={200}
                          filter={{ is_active: true }}
                        >
                          <AutocompleteInput
                            optionText="long_name"
                            options={disableAutofillOptions}
                          />
                        </ReferenceInput>
                      </Grid>
                      {hasWorkplaceServices && (
                        <>
                          <Grid item xs={12}>
                            <FormDataConsumer source="parking_id">
                              {({ formData: innerFormData, ...props }) => (
                                <ReferenceInput
                                  source="parking_id"
                                  reference="parking"
                                  validate={[required()]}
                                  filter={{
                                    client_location_id:
                                      innerFormData.client_location_id,
                                  }}
                                  {...props}
                                >
                                  <ReferenceChild
                                    optionText="name"
                                    source="parking_id"
                                    validate={[required()]}
                                  />
                                </ReferenceInput>
                              )}
                            </FormDataConsumer>
                          </Grid>
                          <Grid item xs={12}>
                            <FormDataConsumer source="keyosk_id">
                              {({ formData: innerFormData, ...props }) => (
                                <ReferenceInput
                                  source="keyosk_id"
                                  reference="keyosk"
                                  validate={[required()]}
                                  filter={{
                                    client_location_id:
                                      innerFormData.client_location_id,
                                  }}
                                  {...props}
                                >
                                  <ReferenceChild
                                    optionText="name"
                                    source="keyosk_id"
                                    validate={[required()]}
                                  />
                                </ReferenceInput>
                              )}
                            </FormDataConsumer>
                          </Grid>
                          <Grid item xs={12}>
                            <TextInput source="box" />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                )}
              </Grid>
            </Grid>
            {actionButtons(handleSubmitWithRedirect, saving, formValues)}
          </>
        )}
      />
    </div>
  );
};

export default AddressForm;
